import { cn } from '@/utils/styles';
import { Card } from '@design-system/Card';
import { Link } from '@design-system/Link';
import { Skeleton } from '@design-system/Skeleton';
import { List, ListItem } from '@design-system/List';

export interface IUserHealthHistory {
  allergies?: string;
  foodSensitives?: string;
  pregnancyStatus?: string;
  currentlyBreastfeeding?: string;
}

export interface MyHealthHistoryPageStatusSectionProps
  extends React.ComponentProps<'div'> {
  isLoading?: boolean;
  healthHistory?: IUserHealthHistory;
  'data-testid'?: string;
}

type IHealthHistoryData = {
  to?: string;
  label: string;
  value?: string | number;
};

const renderUserData = (
  healthHistoryData: IHealthHistoryData[],
  isLoading?: boolean,
) => {
  const renderedUserData = healthHistoryData.map((data) => {
    const itemTestId = `healthHistoryStatus-item-${data.label.toLowerCase().replace(/\s+/g, '')}`;
    
    if (data.to) {
      return (
        <ListItem key={data.label} asChild data-testid={itemTestId}>
          <Link to={data.to}>{data.label}</Link>
        </ListItem>
      );
    }

    return (
      <ListItem key={data.label} hideIndicator data-testid={itemTestId}>
        <span data-testid={`${itemTestId}-label`}>{data.label}</span>

        {isLoading ? (
          <Skeleton className="ml-auto w-full h-4 max-w-24" data-testid={`${itemTestId}-skeleton`} />
        ) : (
          <span className="ml-auto text-secondary truncate" data-testid={`${itemTestId}-value`}>
            <abbr title={String(data.value)} className="no-underline">
              {data.value}
            </abbr>
          </span>
        )}
      </ListItem>
    );
  });

  return renderedUserData;
};

export const MyHealthHistoryPageStatusSection = ({
  isLoading,
  className,
  healthHistory,
  'data-testid': testId,
}: MyHealthHistoryPageStatusSectionProps) => {
  const healthHistoryData = [
    {
      label: 'Pregnancy Status',
      value: healthHistory?.pregnancyStatus,
    },
    {
      label: 'Currently Breastfeeding',
      value: healthHistory?.currentlyBreastfeeding,
    },
    {
      label: 'Current Reported Condition',
      to: '#',
    },
    {
      label: 'Allergies Reported',
      value: healthHistory?.allergies,
    },
    {
      label: 'Food Sensitivities Reported',
      value: healthHistory?.foodSensitives,
    },
  ] satisfies IHealthHistoryData[];

  return (
    <Card className={cn('overflow-hidden', className)} data-testid={testId}>
      <List data-testid="healthHistoryStatus-list">{renderUserData(healthHistoryData, isLoading)}</List>
    </Card>
  );
};
